<script setup lang="ts">
import { LANGUAGES } from "core/constants";
import type { Language } from "core/interfaces";
import { LOCALE } from "core/interfaces";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateUserSettingsDto } from "~/common/interfaces/dto/user-settings/update-user-settings.dto";
import type { UserSettingsForm } from "~/common/interfaces/form/user-settings-form.interface";
import { useAlertStore } from "~/stores/alert";
import { useModalStore } from "~/stores/modal";

const { locale, setLocale, t } = useI18n();
const alertStore = useAlertStore();
const { profileModalEdit } = useModalStore();
const userSettingsStore = useUserSettingsStore();
const { errors, handleSubmit, meta } = useForm({
	initialValues: {
		language: LANGUAGES.find(language => language.locale === locale.value),
		telegramId: profileModalEdit.userSettings.telegramId,
		telegramNotifications: profileModalEdit.userSettings.telegramNotifications
	},
	validationSchema: yup.object({
		language: yup.object({
			id: yup
				.string()
				.required(t("INPUT.language.error.required"))
		}),
		telegramId: yup
			.string()
			.nullable()
			.matches(/^\d*$/, t("INPUT.telegram.error.invalid"))
			.optional(),
		telegramNotifications: yup.boolean().optional()
	})
});

const adaptUserSettingsFormToDto = (form: UserSettingsForm): UpdateUserSettingsDto => {
	return {
		telegramId: form.telegramId || undefined,
		telegramNotifications: form.telegramNotifications
	};
};
const onSubmit = handleSubmit(async (form: UserSettingsForm) => {
	const dto = adaptUserSettingsFormToDto(form);

	await userSettingsStore.patchMe(dto);

	profileModalEdit.closeModal();

	const locale = form.language?.locale || LOCALE.ENGLISH;
	await setLocale(locale);

	alertStore.addAlert({
		title: t("COMPONENTS.PROFILE.MODAL_EDIT.alert.title"),
		description: "",
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: language } = useField<Language>("language");
const { value: telegramId } = useField<string>("telegramId");
const { value: telegramNotifications } = useField<boolean>("telegramNotifications");
useField<string>("language.id");
</script>

<template>
	<AppModal
		:title="$t('COMMON.profile')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="meta.valid"
		@close="profileModalEdit.closeModal"
		@submit="onSubmit"
	>
		<div class="flex flex-col gap-y-6">
			<div>
				<span class="font-semibold text-base">{{ $t('COMMON.generalInfo') }}</span>
				<div class="mt-4 flex flex-col gap-y-2">
					<AppInputSelect
						v-model="language"
						type="text"
						:title="$t('COMMON.language')"
						:placeholder="$t('INPUT.PLACEHOLDER.selectLanguage')"
						options-primary-key="name"
						:options="LANGUAGES"
						:options-settings="{searchOptionsByInput: false}"
						:error="errors.language"
						required
					/>
				</div>
			</div>
			<div>
				<span class="font-semibold text-base">{{ $t('COMMON.notifications') }}</span>
				<div class="mt-4 flex flex-col gap-y-2">
					<AppInput
						v-model="telegramId"
						type="text"
						:title="$t('INPUT.telegram.title')"
						placeholder="1337"
						:error="errors.telegramId"
					/>
					<AppCheckbox
						v-model="telegramNotifications"
						class="mt-2"
					>
						{{ $t('COMMON.subscribeToTelegramBot') }}
					</AppCheckbox>
				</div>
			</div>
		</div>
	</AppModal>
</template>
